import type { InsurancePolicyDocumentModel } from "../models/insurancePolicy";
import { formatDanishLongDate } from "@/util/date";
import {
  appendUnmatchedFailingFields,
  getFieldValidationItem,
} from "./fieldValidationHelpers";
import {
  MIN_GUARANTEE_TEXT,
  MIN_LAWYER_LIABILITY_TEXT,
} from "../constants/insuranceRequirements";
import type { ValidationItem } from "../views/documents/components/DocumentValidationCard";

const MISSING_VALUE = "Ikke angivet";

const currencyFormatter = new Intl.NumberFormat("da-DK", {
  style: "currency",
  currency: "DKK",
  maximumFractionDigits: 0,
});

const formatCurrency = (amount: number | null | undefined): string => {
  if (amount == null) {
    return MISSING_VALUE;
  }

  return currencyFormatter.format(amount);
};

/** See the note on RENDERED_FIELD_KEYS in courseCertificateValidation.ts. */
const RENDERED_FIELD_KEYS = [
  "insuranceCompany",
  "lawyerLiabilityAmount",
  "guaranteeAmount",
  "policyStartDate",
] as const;

const createFallbackRow = (label: string, reason: string): ValidationItem => ({
  label,
  value: MISSING_VALUE,
  isValid: false,
  showValidationState: true,
  reason,
});

export const createInsurancePolicyValidationItems = (
  policy: InsurancePolicyDocumentModel | undefined,
): ValidationItem[] => {
  const items: ValidationItem[] = [
    {
      label: "Forsikringsselskab",
      value: policy?.insuranceCompany ?? MISSING_VALUE,
      ...getFieldValidationItem(policy?.fieldValidation, "insuranceCompany"),
    },
    {
      label: `Advokatansvar (min. ${MIN_LAWYER_LIABILITY_TEXT})`,
      value: formatCurrency(policy?.lawyerLiabilityAmount),
      ...getFieldValidationItem(
        policy?.fieldValidation,
        "lawyerLiabilityAmount",
      ),
    },
    {
      label: `Garantibeløb (min. ${MIN_GUARANTEE_TEXT})`,
      value: formatCurrency(policy?.guaranteeAmount),
      ...getFieldValidationItem(policy?.fieldValidation, "guaranteeAmount"),
    },
    {
      label: "Police startdato",
      value: formatDanishLongDate(policy?.policyStartDate, MISSING_VALUE),
      ...getFieldValidationItem(policy?.fieldValidation, "policyStartDate"),
    },
  ];

  return appendUnmatchedFailingFields(
    items,
    policy?.fieldValidation,
    RENDERED_FIELD_KEYS,
    createFallbackRow,
  );
};
